<template>
  <div
    class="bg-white border rounded-2xl p-3 shadow-sm transition-colors"
    :class="selected ? 'border-primary-400 ring-1 ring-primary-200' : 'border-gray-200'"
  >
    <div class="flex items-start gap-3">
      <Checkbox
        v-if="selectable"
        :modelValue="selected"
        binary
        class="mt-1 flex-shrink-0"
        @update:modelValue="emit('toggle-select')"
      />

      <!-- Identity -->
      <button type="button" class="flex-1 min-w-0 text-right" @click="emit('open')">
        <div class="flex items-center gap-1.5">
          <span
            class="w-2 h-2 rounded-full flex-shrink-0"
            :style="{ backgroundColor: PRIORITY_COLORS[lead.priority || 'medium'] }"
            :title="PRIORITY_LABELS[lead.priority || 'medium']"
          />
          <span class="font-semibold text-gray-900 truncate">{{ lead.businessName }}</span>
          <Tag v-if="lead.isTestBusiness" value="בדיקה" severity="warn" class="!text-[9px]" />
        </div>

        <div class="flex items-center gap-2 text-xs text-gray-500 mt-0.5 flex-wrap">
          <span v-if="lead.contactName">{{ lead.contactName }}</span>
          <span v-if="lead.phone" dir="ltr" class="font-mono text-[11px]">{{ lead.phone }}</span>
        </div>
      </button>

      <!-- Status -->
      <span
        class="flex items-center gap-1 px-2 h-6 rounded-full border text-[11px] font-medium flex-shrink-0"
        :class="STAGE_CHIP_CLASSES[lead.stage]"
      >
        <i :class="['pi', STAGE_ICONS[lead.stage], 'text-[9px]']" />
        {{ STAGE_LABELS[lead.stage] }}
      </span>
    </div>

    <!-- Registered business: is this a real customer, and are they using it -->
    <RouterLink
      v-if="lead.business"
      :to="{ name: PlatformRouteNames.BusinessView, params: { bid: lead.business.bid } }"
      class="block mt-2 bg-gray-50 hover:bg-gray-100 rounded-xl px-3 py-2 transition-colors"
    >
      <div class="flex items-center gap-2 text-xs">
        <i class="pi pi-building text-gray-400 text-xs flex-shrink-0" />
        <span class="text-gray-700 font-medium truncate">{{ lead.business.title }}</span>
        <span
          class="px-2 h-5 rounded-full text-[10px] font-medium flex items-center whitespace-nowrap flex-shrink-0"
          :class="planChip.class"
        >
          {{ planChip.label }}
        </span>
        <i class="pi pi-chevron-left text-gray-300 text-[10px] mr-auto flex-shrink-0" />
      </div>

      <!-- The numbers that say whether this business is actually alive -->
      <div class="flex items-center flex-wrap gap-x-3 gap-y-1 mt-1.5 text-[11px] text-gray-500">
        <span v-if="signupLabel" class="whitespace-nowrap">נרשם {{ signupLabel }}</span>
        <span class="whitespace-nowrap">{{ lead.business.servicesCount ?? 0 }} שירותים</span>
        <span class="whitespace-nowrap">{{ lead.business.appointmentsTotal }} תורים</span>
        <span
          class="whitespace-nowrap font-medium"
          :class="lead.business.appointmentsLast30d ? 'text-emerald-600' : 'text-gray-300'"
        >
          {{ lead.business.appointmentsLast30d }} ב-30 יום
        </span>
        <span v-if="lead.business.upcomingAppointments" class="whitespace-nowrap text-teal-600">
          {{ lead.business.upcomingAppointments }} עתידיים
        </span>
      </div>
    </RouterLink>
    <div
      v-else-if="lead.bid"
      class="flex items-center gap-2 mt-2 bg-gray-50 rounded-xl px-3 py-2 text-xs text-gray-400"
    >
      <i class="pi pi-building text-xs" />
      <span class="font-mono">{{ lead.bid }}</span>
    </div>

    <!-- Waiting for -->
    <div v-if="lead.requestedFeatures?.length" class="flex flex-wrap gap-1 mt-2">
      <button
        v-for="f of lead.requestedFeatures.slice(0, 3)"
        :key="f"
        type="button"
        class="text-[10px] bg-primary-50 text-primary-700 border border-primary-100 px-2 py-0.5 rounded-full"
        @click.stop="emit('filter-feature', f)"
      >
        {{ f }}
      </button>
      <span v-if="lead.requestedFeatures.length > 3" class="text-[10px] text-gray-400 self-center">
        +{{ lead.requestedFeatures.length - 3 }}
      </span>
    </div>

    <!-- Open task -->
    <div
      v-if="lead.nextTask"
      class="flex items-center gap-2 mt-2 text-xs"
      :class="taskOverdue ? 'text-red-600' : 'text-gray-500'"
    >
      <i class="pi pi-flag text-[10px]" />
      <span class="truncate">{{ lead.nextTask.title }}</span>
      <span class="whitespace-nowrap">· {{ relativeDate(lead.nextTask.dueDate) }}</span>
    </div>

    <!-- Footer: history + the actions -->
    <div class="flex items-center justify-between gap-2 mt-3">
      <div class="flex items-center gap-2 text-[11px] text-gray-400 min-w-0">
        <span :class="{ 'text-red-500 font-medium': stale }">
          {{ lastContactLabel }}
        </span>
        <template v-if="lead.noAnswerCount">
          <span>·</span>
          <span class="text-amber-600">{{ lead.noAnswerCount }} ניסיונות ללא מענה</span>
        </template>
        <template v-if="lead.assignedTo">
          <span>·</span>
          <span class="text-gray-500">{{ assigneeLabel(lead.assignedTo) }}</span>
        </template>
      </div>

      <div class="flex items-center gap-1.5 flex-shrink-0">
        <a
          v-if="lead.phone"
          :href="`tel:${toDialable(lead.phone)}`"
          class="w-9 h-9 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center active:bg-blue-100"
          @click.stop
        >
          <i class="pi pi-phone text-xs" />
        </a>
        <a
          v-if="lead.phone"
          :href="`https://wa.me/${toWhatsappNumber(lead.phone)}`"
          target="_blank"
          rel="noopener"
          class="w-9 h-9 rounded-xl bg-green-50 text-green-600 flex items-center justify-center active:bg-green-100"
          @click.stop
        >
          <i class="pi pi-whatsapp text-xs" />
        </a>
        <button
          v-if="lead.stage !== PlatformLeadStageEnum.Converted"
          type="button"
          class="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center active:bg-emerald-100"
          title="הומר ללקוח — יוסתר מהרשימה"
          @click.stop="emit('convert')"
        >
          <i class="pi pi-verified text-xs" />
        </button>
        <button
          type="button"
          class="h-9 px-3 rounded-xl bg-primary-600 text-white text-xs font-medium flex items-center gap-1.5 active:bg-primary-700"
          @click.stop="emit('log')"
        >
          <i class="pi pi-pencil text-[10px]" />
          סכם
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { DateTime } from 'luxon';
import { PlatformLeadStageEnum, type IPlatformLeadWithStats } from '@torim-app/types';
import { PlatformRouteNames } from '../../router/route-names';
import {
  PRIORITY_COLORS,
  PRIORITY_LABELS,
  STAGE_CHIP_CLASSES,
  STAGE_ICONS,
  STAGE_LABELS,
  assigneeLabel,
  isLeadStale,
  relativeDate,
  toDialable,
  toWhatsappNumber,
} from '../../composables/useLeadDisplay';

const props = defineProps<{
  lead: IPlatformLeadWithStats;
  selectable?: boolean;
  selected?: boolean;
}>();

const emit = defineEmits<{
  (e: 'open'): void;
  (e: 'log'): void;
  (e: 'convert'): void;
  (e: 'toggle-select'): void;
  (e: 'filter-feature', feature: string): void;
}>();

const stale = computed(() => isLeadStale(props.lead));

const lastContactLabel = computed(() =>
  props.lead.lastContactedAt ? `דובר ${relativeDate(props.lead.lastContactedAt)}` : 'לא דובר מעולם',
);

const taskOverdue = computed(
  () => !!props.lead.nextTask && new Date(props.lead.nextTask.dueDate).getTime() < Date.now(),
);

const signupLabel = computed(() => {
  const date = props.lead.business?.createdAt || props.lead.signupDate;
  return date ? DateTime.fromJSDate(new Date(date)).toFormat('dd/MM/yy') : '';
});

/** Plan + contract state in one chip: paying, on trial (with days left), or expired */
const planChip = computed<{ label: string; class: string }>(() => {
  const biz = props.lead.business;
  if (!biz) return { label: '', class: '' };

  if (biz.contractStatus === 'trial') {
    const daysLeft = biz.trialEndDate
      ? Math.ceil(DateTime.fromJSDate(new Date(biz.trialEndDate)).diff(DateTime.local(), 'days').days)
      : null;
    if (daysLeft !== null && daysLeft <= 0) {
      return { label: 'ניסיון הסתיים', class: 'bg-red-50 text-red-600 border border-red-100' };
    }
    return {
      label: daysLeft !== null ? `ניסיון · ${daysLeft} ימים` : 'ניסיון',
      class: 'bg-amber-50 text-amber-700 border border-amber-100',
    };
  }

  if (biz.contractStatus === 'expired') {
    return { label: 'פג תוקף', class: 'bg-red-50 text-red-600 border border-red-100' };
  }

  if (biz.plan === 'premium') {
    return { label: 'פרימיום', class: 'bg-emerald-50 text-emerald-700 border border-emerald-100' };
  }

  return { label: 'חינמי', class: 'bg-gray-100 text-gray-500 border border-gray-200' };
});
</script>
